import { useEffect, useState } from "react";
import type { ReferenceImagePayload } from "./types";

export function readFileAsBase64(file: File): Promise<ReferenceImagePayload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const [header, base64] = dataUrl.split(",", 2);
      const mimeMatch = header?.match(/^data:(.*);base64$/);
      if (!base64 || !mimeMatch) {
        reject(new Error("Failed to read the uploaded image"));
        return;
      }
      resolve({ mimeType: mimeMatch[1], base64 });
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read the uploaded image"));
    reader.readAsDataURL(file);
  });
}

export function useImagePayload(file: File | null): { previewUrl: string | null; payload: ReferenceImagePayload | null; error: boolean } {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [payload, setPayload] = useState<ReferenceImagePayload | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    setPayload(null);
    if (!file) {
      setError(false);
      return;
    }
    let cancelled = false;
    setError(false);
    readFileAsBase64(file)
      .then((p) => {
        if (!cancelled) setPayload(p);
      })
      .catch(() => {
        if (!cancelled) {
          setPayload(null);
          setError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [file]);

  return { previewUrl, payload, error };
}
